import logger from '../../utils/logger-rum';
import React, {
	useCallback, useEffect, useMemo, useRef, useState, useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import { VideoStream } from '../types/videos';
import type { Invitation, Profile } from '../../stores/types/invitations';
import { ConnectedUser } from '../../stores/types/connection';
import blackImage from '../../images/black-image.jpeg';
import { cleanupVideoStreamElement } from '../../utils/video/video-streams';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import type { DropCallback } from '../../stores/types/user-interface';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import useShots from '../../contexts/shots/provider';
import EmptyScreenShare from './empty-screen-share';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { SOCKET_MESSAGE_TYPES } from '../../types/constants';
import { SocketContext } from '../../contexts/socket';

interface Props {
	mediaId: string | number;
	feedPosition: number;
}

export default function FeedScreenShare(props: Props) {
	const { feedPosition, mediaId: email } = props;
	const isEmbed = useSelector((s: GS) => s.SessionReducer.embed);
	const meetingId = useSelector((s: GS) => s.SessionReducer.meeting_id);
	const connectedPresenterVideos = useSelector(
		(state: GS) => state.VideoReducer.connectedPresenterVideos,
	);
	const connectedUsers = useSelector(
		(state: GS) => state.SessionReducer.connectedUsers,
	);

	const { socket } = useContext(SocketContext);
	const { idsOfVideosInCanvas } = useShots();

	const [pollingAttempts, setPollingAttempts] = useState(0);

	const hasWarnedMissingScreenShare = useRef(false);

	const getScreenShareFromEmail = useCallback((emails: string) => {
		const cUser = connectedUsers.find(
			(connectedUser: ConnectedUser) => connectedUser.email === emails,
		);

		if (!cUser?.screenShareWebrtcId) return undefined;

		return connectedPresenterVideos.find(
			(vid: VideoStream) => vid.id === cUser.screenShareWebrtcId,
		);
	}, [connectedUsers, connectedPresenterVideos]);

	const screenShareVideo = getScreenShareFromEmail(email as string);

	// Polling effect for screen share video
	useEffect(() => {
		if (screenShareVideo) {
			// Video found, stop polling and re-arm the alert for any later outage
			setPollingAttempts(0);
			hasWarnedMissingScreenShare.current = false;
			return;
		}

		const maxAttempts = 10; // Poll for up to 10 attempts
		const pollInterval = 1000; // 1 second intervals

		if (pollingAttempts >= maxAttempts) {
			if (hasWarnedMissingScreenShare.current) return;

			hasWarnedMissingScreenShare.current = true;
			logger.warn('Max polling attempts reached for screen share video', {
				email,
				meetingId,
				isEmbed,
				attempts: pollingAttempts,
			});
			if (socket && isEmbed) {
				socket.sendMessage(SOCKET_MESSAGE_TYPES.GREENROOM_ALERT, {
					message: 'No Screen Share Available',
					description: `Screen share is not available in embed view for ${email}`,
					duration: 5000,
					type: 'warning',
					host: true, // Only show to hosts
				});
			}
			return;
		}

		const timer = setTimeout(() => {
			setPollingAttempts((prev) => prev + 1);
		}, pollInterval);

		return () => clearTimeout(timer);
	}, [screenShareVideo, pollingAttempts, email, meetingId, isEmbed, socket]);

	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const videoElRef = useRef<HTMLVideoElement>(null);

	const isOnCanvas = useMemo(
		() => screenShareVideo && idsOfVideosInCanvas.includes(screenShareVideo.id),
		[screenShareVideo, idsOfVideosInCanvas],
	);

	const userFromEmail = useMemo(() => {
		const connectedUser = connectedUsers.find((user: ConnectedUser) => user.email === email);
		if (connectedUser) return connectedUser.gr_user;
		const invite = invitations.find((invitation: Invitation) => invitation.profile.email === email);
		if (invite?.profile.gr_user) return invite.profile.gr_user;
		return 0;
	}, [email, connectedUsers, invitations]);

	useEffect(() => {
		if (videoElRef.current && screenShareVideo
			&& screenShareVideo?.srcObject !== videoElRef.current.srcObject) {
			videoElRef.current.srcObject = screenShareVideo.srcObject;
			videoElRef.current.volume = 0;
			if (!screenShareVideo.srcObject.active) return;
			videoElRef.current.play().then(() => {
				if (videoElRef.current?.paused) {
					videoElRef.current.play().catch((e) => {
						logger.error('video el play request error', {
							location: 'feed-screenshare second',
						}, e);
					});
				}
			}).catch((e) => {
				logger.error('video el play request error', {
					location: 'feed-screenshare first',
				}, e);
			});
			cleanupVideoStreamElement(videoElRef.current);
		}
	}, [screenShareVideo?.srcObject]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || isEmbed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	return (
		screenShareVideo && screenShareVideo?.srcObject.active
			? <>
					<video
						key={screenShareVideo.srcObject.id + feedPosition}
						muted={true}
						autoPlay={true}
						poster={blackImage}
						playsInline
						ref={videoElRef}
					>
					</video>
			</>
			: <>
					<EmptyScreenShare grUserId={userFromEmail} />
				</>
	);
}
